/**
 * Utility for fetching Azure blob content.
 *
 * The server returns API-relative URLs (starting with /api/Files/) for Azurite
 * (local emulator) to avoid CORS issues. This utility resolves those to full URLs
 * using the configured API domain.
 *
 * For production Azure blob URLs with SAS tokens, fetches directly.
 */

import { defaultDomain } from '../api/default-domain';
import { ApiFactory } from '../api/agwasuite-api-factory';

/**
 * Checks if a URL is an API-relative path (returned by server for Azurite).
 */
export function isApiRelativeUrl(url: string): boolean {
  return url.startsWith('/api/');
}

/**
 * Resolves a blob URL to a fetchable URL.
 * - API-relative URLs (/api/Files/...) are prefixed with the API domain
 * - Full URLs (https://...) are returned as-is
 */
export function resolveBlobUrl(url: string): string {
  if (isApiRelativeUrl(url)) {
    const apiBase = defaultDomain ?? window.location.origin;
    return `${apiBase}${url}`;
  }
  return url;
}

/**
 * The `src` for a user's avatar, from whatever the user directory happens to be holding.
 *
 * THREE RENDER SITES USED TO SPELL THIS THEMSELVES, all as
 * `url.startsWith('http') ? url : `${defaultDomain}${url}``, and that rule is wrong for anything
 * that is neither absolute nor API-relative. A freshly uploaded avatar is a `data:` URL - the bytes
 * the upload just sent, shown immediately because the stored URL is deterministic and would never
 * trigger a refetch - and prefixing it produced `https://host/data:image/jpeg;base64,...`, which
 * renders as a broken image. Observed 2026-08-26, in all three places at once, because it was the
 * same mistake copied.
 *
 * Prefixes ONLY an API-relative path. Absolute URLs and data URLs are already complete and are
 * returned untouched.
 */
export function resolveAvatarUrl(
  url: string | null | undefined
): string | null {
  if (!url) return null;
  return resolveBlobUrl(url);
}

export interface BlobFetchOptions {
  /**
   * File access token for authorization (for FileCollection API-proxied requests).
   * If provided, added as query parameter for /api/Files/ endpoints.
   */
  fileAccessToken?: string;

  /**
   * Whether to include session authentication headers.
   * Defaults to true for API-relative URLs (excludes /api/Files/ which uses fileAccessToken).
   */
  includeSessionAuth?: boolean;
}

/**
 * Fetches blob content, resolving API-relative URLs to the correct domain.
 *
 * For production Azure blob URLs with SAS tokens, fetches directly.
 * For API-relative URLs (Azurite/development), prefixes with API domain
 * and includes appropriate authentication.
 *
 * @param url - The blob URL (may be API-relative or production Azure)
 * @param options - Additional options including file access token
 * @returns Fetch Response
 */
export async function fetchBlob(
  url: string,
  options?: BlobFetchOptions
): Promise<Response> {
  let resolvedUrl = resolveBlobUrl(url);
  const headers: Record<string, string> = {};

  if (isApiRelativeUrl(url)) {
    // For /api/Files/ endpoints, use fileAccessToken query param
    if (url.startsWith('/api/Files/') && options?.fileAccessToken) {
      const urlObj = new URL(resolvedUrl);
      urlObj.searchParams.set('fileAccessToken', options.fileAccessToken);
      resolvedUrl = urlObj.toString();
    } else {
      // For other API endpoints (e.g., /api/sandbox/), use session auth
      const includeAuth = options?.includeSessionAuth !== false;
      if (includeAuth) {
        // TWO HEADERS: `X-Session-Id` carries the ACCOUNT KEY, which is what selects the auth
        // cookie server-side - send the tab session here and authentication fails outright.
        // `X-AGWA-Tab-Session` carries the per-tab session that lock ownership is judged by (#362).
        const accountKey = ApiFactory.getAccountKey();
        if (accountKey) {
          headers['X-Session-Id'] = accountKey;
        }
        const tabSessionId = ApiFactory.getSessionId();
        if (tabSessionId) {
          headers['X-AGWA-Tab-Session'] = tabSessionId;
        }
      }
    }
  }

  const fetchOptions: RequestInit = isApiRelativeUrl(url)
    ? {
        credentials: 'include', // Include auth cookies for API requests
        headers,
      }
    : {};

  return fetch(resolvedUrl, fetchOptions);
}

/**
 * Fetches JSON from a blob URL, resolving API-relative URLs.
 */
export async function fetchBlobJson<T = unknown>(
  url: string,
  options?: BlobFetchOptions
): Promise<T> {
  const response = await fetchBlob(url, options);
  if (!response.ok) {
    throw new Error(`Failed to fetch blob: ${response.status}`);
  }
  return response.json();
}
